/* eslint-disable no-restricted-globals */
import { type ColumnState } from "ag-grid-enterprise";
import moment from "moment";
import { type IAskRecentQuestion } from "searchAsk/askSuggest";
import { type IStoredAskTranscript } from "searchAsk/askTranscript.types";
// eslint-disable-next-line no-restricted-imports
import { useReadLocalStorage as useReadLocalStorageInternal } from "usehooks-ts";

import type { ProposalDisplayColumnTypes } from "@btnet/services-btnet/handlers/models/proposalDisplayColumnTypes";
import type { SubmittalExtractionStatus } from "@btnet/services-btnet/handlers/models/submittalExtractionStatus";

import { type BTLoginTypes, type DirectoryType, EntityTypes } from "types/enum";

import { type ISawmillEvent } from "utilities/analytics/customPlugins/sawmillPlugin";
import { isUnitTest } from "utilities/environment/environment";
import { reportError } from "utilities/errorHelpers";
import type { IStepUpContext } from "utilities/stepUpAuth/stepUpContext.types";

import { type IOAuthStateParamState } from "commonComponents/auth/AuthStoreDiscovery.types";
import { type IEditorApiType } from "commonComponents/btWrappers/editor/editor.types";
import { type IAutoDismissEmptyState } from "commonComponents/entity/emptyState/common/EmptyState.api.types";
import { type ILastSelectedInvoicePreferences } from "commonComponents/entity/invoicing/ExistingOwnerInvoiceSelect/ExistingOwnerInvoiceSelect.api.types";
import { type IAddInvoiceLineItemPreferences } from "commonComponents/entity/invoicing/LineItemsToInvoice/LineItemsToInvoice.api.types";
import { type IProcessStatusGroupState } from "commonComponents/utilities/ClientObservableBackgroundJobStatusPopup/ClientObservableBackgroundJobStatusPopup.types";
import { type IHeaderInfoResponse } from "commonComponents/utilities/HeaderInfo/OwnerHeaderInfo.api.types";
import { type IJobPickerPersistantState } from "commonComponents/utilities/JobPicker/JobPicker.types";
import { type GlobalSearchResult } from "commonComponents/utilities/MainNavigation/globalSearch/GlobalSearch.types";
import { type ISearchDialogRecent } from "commonComponents/utilities/MainNavigation/SearchDialog/browse/recentItems";
import { type ISearchBarResultResponse } from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.api.types";

import { type TabsFilterTabs } from "entity/bill/BillList/TabsFilter/TabsFilter";
import { type IPersistedBillUpload } from "entity/bill/BillsInbox/billUploadQueue.types";
import { type ExpectedBudgetReportOption } from "entity/budget/Budget/BudgetContainer/BudgetContainer.api.types";
import { type IPreviousAuthStoreInfo } from "entity/login/EmailUsernameEntry/EmailUsernameEntry.types";
import { type IOAuthStateValues } from "entity/login/Login/Login.api.types";
import { type IMediaListSort, type SelectedMediaListViews } from "entity/media/common/mediaTypes";
import {
    type NotificationPermissionPromptState,
    PromptState,
} from "entity/notification/notification.types";
import { type NotificationPanelTab } from "entity/notification/panel/NotificationPanel";
import { type IOprUserPreferences } from "entity/onlinePayment/OnlinePaymentGroupedList/OnlinePaymentGroupedList.api.types";
import { type ICheckOutReferralState } from "entity/ownerInvoice/OwnerInvoiceCheckout/OwnerInvoiceCheckout.api.types";
import { type IPendingWebviewLineItemsToInvoiceEntry } from "entity/ownerInvoice/OwnerInvoiceWebview/pendingWebviewLineItemsToInvoice";
import { type ICachedReceipt } from "entity/rebateReceiptReview/hooks/useCachedReceipts";
import {
    DEFAULT_GRID_WIDTH,
    GanttColumnType,
    GanttZoomLevels,
    type IGanttChartLocalSettings,
} from "entity/schedule/Gantt/GanttChart/GanttChart.types";
import { type IGanttPrintState } from "entity/schedule/Gantt/GanttPrint/GanttPrint.types";
import { type SelectionOptionsViewMode } from "entity/selectionOption/SelectionOptionsGalleryView/selectionOptionsViewPreference";
import { type SpecificationViewPreference } from "entity/specification/common/specificationViewPreference";
import { ShiftGroupBy } from "entity/timeClock/ShiftList/ShiftGrid/ShiftGrid.api.types";
import { type ITradeAgreementSessionValues } from "entity/tradeAgreement/TradeAgreement/TradeAgreement.api.types";

// Re-export IStepUpContext for convenience
export type { IStepUpContext };

// todo - remove this, these will all be migrated to ILocalStorage
export enum LocalStorageKeys {
    MainNavigationData = "mainNavigationData",
    CurrentJobPickerFilter = "currentJobPickerFilter",
    SelectedJobPickerFilterId = "selectedJobPickerFilterId",
    JobPickerKeywordSearch = "jobPickerKeywordSearch",
    GanttChartLocalSettings = "ganttChartLocalSettings",
    DynamicServiceCloudKey = "_WEB_STORAGE",
}

interface IFilterPreferences {
    /**
     * true if panel is open, false if panel is closed
     * undefined means user has no preferences saved
     */
    [key: number]: boolean | undefined;
}

interface IPageSizePreferences {
    /**
     * key should be ListEntityType
     */
    [key: number]: number | undefined;
}

export interface IProcessStatusStorage {
    /**
     * key should be ProcessStatusGroupType
     */
    [key: number | string]: IProcessStatusGroupState;
}

/**
 * One persisted AI submittal extraction. Stored per (builderId, jobId, userId) so the
 * processing / ready popup re-appears when the user returns to the same job's
 * submittals list after navigating away. <c>dismissed</c> sticks an explicit X click
 * so the popup doesn't reappear for that extraction.
 */
export interface IPersistedSubmittalExtraction {
    extractionGuid: string;
    jobId: number;
    specFileName?: string;
    drawingFileName?: string;
    // Last-known terminal status (Completed or Failed). When set, the tracker skips
    // the initial Scanning render and seeds itself with this status — preserves the
    // Failed state across navigation so a failed run doesn't restore as Ready.
    seededStatus?: SubmittalExtractionStatus;
    // Server-generated CSV filename captured at terminal time. Persisted alongside
    // seededStatus so a restored Completed run renders the Download CSV button
    // immediately on mount; without it, the button is gated behind the next poll
    // round-trip, which fails the UX when the poll is slow or errors out.
    seededGeneratedFileName?: string;
    // Whether the completed run found zero submittals. Persisted so a restored run shows the
    // "No submittals found" view immediately instead of flashing the Ready header first.
    seededNoSubmittalsFound?: boolean;
    // Set once polling gives up after a sustained outage, so a refresh restores straight into the
    // error view without polling again. Cleared only when a poll succeeds.
    seededPollFailed?: boolean;
    dismissed?: boolean;
    createdAtMs: number;
}

/**
 * Note to devs: do NOT store classes in LocalStorage, use interfaces instead.
 * Reminder only some data can be stringified and stored (see detectDataThatCantBeStored for more info).
 * See https://btwiki.atlassian.net/wiki/spaces/dv/pages/3366355107/Browser+Storage+Patterns for more details
 */
export interface ILocalStorage {
    "bt-boolean-worksheetTaxBannerDismissed": boolean;
    "bt-boolean-btMFAWarningBannerDismissed": boolean;
    // Dismissing the selections trial invitation on the selections list hides it there only. The
    // copy of it in selections settings is not dismissible, so the feature stays discoverable for
    // an account owner who goes looking for it.
    "bt-boolean-selectionsTrialAlertDismissed": boolean;
    "bt-boolean-hideScheduleAlerts": boolean;
    "bt-boolean-logoCollapsed": boolean;
    "bt-boolean-showSchedulePhases": boolean;
    "bt-boolean-showScheduleNotes": boolean;
    "bt-boolean-hideEditorDebug": boolean;
    "bt-boolean-obpDontShowAgain": boolean;
    "bt-boolean-hideClientUpdateClientVisibilityDisclaimer": boolean;
    "bt-boolean-completedSubtasksOpen": boolean;
    "bt-boolean-taskAssistantVisible": boolean;
    // ATs set this to true to force-disable analytics
    "bt-boolean-disableAllAnalytics": boolean | null;
    "bt-boolean-hasGivenEnterpriseMarketingFeedback": boolean | null;
    "bt-boolean-hasUnreadNotifications": boolean | null;
    "bt-number-lastOauthBuilderSelected": number | null;
    "bt-number-recentLoginType": BTLoginTypes | undefined;
    "bt-number-sidebarWidth": number;
    "bt-object-autoDismissEmptyState": IAutoDismissEmptyState;
    "bt-object-filterPreferences": IFilterPreferences;
    "bt-object-ganttChartLocalSettings": IGanttChartLocalSettings;
    /**
     * Transient gantt render-state handed to the standalone print tab, keyed by a temporary id.
     * The print page reads its entry once and deletes it. See IGanttPrintState.
     */
    "bt-object-ganttPrintState": Record<string, IGanttPrintState>;
    "bt-object-headerInfo": IHeaderInfoResponse | null;
    "bt-object-pageSizePreferences": IPageSizePreferences;
    "bt-object-clientObservableProcessStatusStorage": IProcessStatusStorage;
    /**
     * Active AI submittal extraction state per (builderId, jobId, userId). Lets the
     * processing/ready popup persist across navigation within the same job's submittals
     * list — the user can drill into a submittal detail and come back while the LLM run
     * is still in progress and the popup re-appears. Key shape: "{builderId}:{jobId}:{userId}".
     * `dismissed: true` sticks so an explicitly-dismissed Ready popup doesn't reappear.
     */
    "bt-object-submittalExtractionActive": Record<
        string,
        IPersistedSubmittalExtraction | undefined
    >;
    "bt-object-chatDraftValues": Record<string, IEditorApiType>;
    /**
     * Bills inbox uploads still processing, failed, or created a bill not yet viewed, keyed
     * "{builderId}:{userId}", so the inbox's processing cards and New badges survive a refresh or
     * leaving the page. Items still uploading are
     * not stored - a File can't be persisted, so an interrupted upload can't be resumed.
     */
    "bt-object-billUploadQueue": Record<string, IPersistedBillUpload[] | undefined>;
    /**
     * The in-flight invoice-costs session for the webview "Add to existing Invoice" flow
     * (ado-288112): `ExistingOwnerInvoiceSelect` pushes an absolute webview route, but
     * `ILineItemsToInvoiceDetails` holds `moment.Moment`s and a `Map`, neither cloneable nor
     * JSON-serializable as-is, so it's stored here (pre/post transformed to a localStorage-safe
     * shape) and read back by `OwnerInvoiceWebviewContainer` via a UUID carried in the URL. A single
     * slot: only one session is ever in flight, since the navigation it feeds reads it immediately
     * and the next session overwrites it. The stored `invoiceCostsSessionId` must match the one in
     * the URL, so a stale or abandoned entry can never be picked up by an unrelated navigation.
     */
    "bt-object-pendingWebviewLineItemsToInvoice": IPendingWebviewLineItemsToInvoiceEntry | null;
    /**
     * ado-178532 we had massive event sizes put on this key which made it unsafe
     * @deprecated Use bt-objectArray-sawmillEventsV2
     */
    "bt-objectArray-sawmillEvents": ISawmillEvent[];
    "bt-objectArray-sawmillEventsV2": ISawmillEvent[];
    "bt-objectArray-searchBarRecentResults": ISearchBarResultResponse[];
    "bt-objectArray-globalSearchRecentlyViewed": GlobalSearchResult[];
    /**
     * Records opened from the unified search dialog. Separate from
     * bt-objectArray-globalSearchRecentlyViewed because that one is keyed on the
     * legacy SearchCategory enum, which the dialog's entity routing cannot use.
     */
    "bt-objectArray-searchDialogRecents": ISearchDialogRecent[];
    /** Questions asked on the Ask page, offered back as they are typed again. */
    "bt-objectArray-searchAskRecentQuestions": IAskRecentQuestion[];
    "bt-string-jobPickerPreference": "open" | "closed";
    "bt-string-monthViewPreference": "collapsed" | "expanded";
    "bt-string-selectedDocumentListView": SelectedMediaListViews;
    "bt-string-selectedPhotoListView": SelectedMediaListViews;
    "bt-string-selectedVideoListView": SelectedMediaListViews;
    /**
     * User's saved media list sort per media type, keyed by directory type so each context
     * (Standard, Attached Files, Trash, etc.) is remembered independently.
     */
    "bt-object-selectedPhotoSort": Partial<Record<DirectoryType, IMediaListSort>>;
    "bt-object-selectedDocumentSort": Partial<Record<DirectoryType, IMediaListSort>>;
    "bt-object-selectedVideoSort": Partial<Record<DirectoryType, IMediaListSort>>;
    "bt-string-selectedBrowseBTListView": SelectedMediaListViews;
    "bt-string-selectedViewAllAttachmentsListView": SelectedMediaListViews;
    "bt-string-selectedAttachmentsContainerView": SelectedMediaListViews;
    "bt-string-hideBrowserNotSupportedTime": string | null;
    "bt-object-showMoreState": {
        [key in EntityTypes]?: boolean;
    };
    "bt-object-notificationPermissionsPromptState": NotificationPermissionPromptState;
    "bt-object-btCollapsePrefs": { [key: string]: { values: string[] } } | {};
    "bt-boolean-projectedCostTypeConfirmHidden": boolean;
    "bt-boolean-currentCostsWarningHidden": boolean;

    // Eventually, post SPA, these should be moved to session storage to support multi-tab better
    "bt-object-dangerousJobPickerState": IJobPickerPersistantState;
    "bt-string-paymentVerificationOauthRedirectUrl": string | null;
    "bt-string-paymentServiceAuthToken": string | null;
    "bt-object-oauthRedirectState": IOAuthStateValues | null;
    "bt-boolean-hasUnreadChat": boolean | null;
    "bt-numberArray-jobIDsToPrint": number[] | null;
    "bt-numberArray-proposalCollapsedGroups": number[];
    "bt-object-tableSettings": { [key: string]: { padding: number; fontSize: number } } | {};
    "bt-number-expectedBudgetReportOption": ExpectedBudgetReportOption | null;
    "bt-boolean-debugReactQuery": boolean | null;
    "bt-boolean-showOwnerInvoiceEditOptions": boolean | null;
    "bt-boolean-hidePostBTPaymentsBanner": boolean | null;
    "bt-number-selectedProposalTemplateID": string | null;
    "bt-boolean-hideOutOfSyncAcctBanner": boolean | null;
    "bt-objectArray-previouslyViewedReceipts": ICachedReceipt[];
    "bt-objectArray-recentlyReviewReceipts": ICachedReceipt[];
    "bt-numberArray-proposalFavoritedOptions": number[];
    "bt-object-oAuthStateParam": IOAuthStateParamState | null;
    "bt-string-newAppDetectedDate": number | null;
    "bt-object-oprUserPreferences": IOprUserPreferences | null;
    "bt-object-lastSelectedInvoicePreferences": ILastSelectedInvoicePreferences | null;
    "bt-object-addInvoiceLineItemPreferences": IAddInvoiceLineItemPreferences | null;
    "bt-object-previousAuthStoreInfo": IPreviousAuthStoreInfo | null;
    "bt-string-smsAccessToken": string | null;
    "bt-string-smsAccessTokenLastFour": string | null;
    "bt-boolean-useDraftMode": boolean | null;
    "bt-object-entityReactionMap": Record<string, boolean>;
    "bt-string-selectedNotificationTab": NotificationPanelTab | null;
    "bt-number-selectedBuilderId": number | null;
    "bt-number-billLeftColumnWidth": number;
    "bt-number-billFileAttachmentsColumnWidth": number;
    "bt-number-progressInvoiceSupportingDocsColumnWidth": number;
    "bt-number-billSidePanelColumnWidth": number;
    "bt-boolean-billFileAttachmentsColumnCollapsed": boolean;
    "bt-number-invoiceCustomizeDrawerWidth": number;
    "bt-boolean-invoiceCustomizeDrawerCollapsed": boolean;
    /**
     * The selected jobsite per job picker mode, so switching between jobs and templates can
     * restore what was last selected in the mode being switched to. The legacy un-keyed shape is
     * still read so a selection stored before this existed is not lost.
     */
    "bt-object-selectedJobsiteIdWithBuilder":
        | {
              jobs?: { jobId: number; builderId: number };
              templates?: { jobId: number; builderId: number };
          }
        | { jobId: number; builderId: number }
        | null;
    "bt-object-quickAddClickCounts": Record<string, number>;
    "bt-numberArray-changeOrderColumnDisplayPreferences": ProposalDisplayColumnTypes[] | null;
    "bt-string-planViewerSidebarTab": "thumbnails" | "pages";
    "bt-boolean-planViewerShowAnnotations": boolean;
    "bt-string-billDefaultTab": TabsFilterTabs | null;
    /**
     * Builder's last explicit Book/List choice for the Specifications area. `null` until they use
     * the toggle, which leaves the persona default in charge.
     */
    "bt-string-specificationViewPreference": SpecificationViewPreference | null;
    "bt-object-billLineItemContainerV2ColumnState": ColumnState[] | null;
    "bt-object-selectionOptionsColumnState": Record<string, ColumnState[]> | null;
    /** Last view picked with the New Selections list/gallery toggle. `null` means list. */
    "bt-string-selectionOptionsViewPreference": SelectionOptionsViewMode | null;
    /**
     * Per-user Job Costing Budget grid column layout (order, width, visibility, pinning), keyed by
     * a `"{builderId}:{globalUserId}"` subKey so a user's layout is shared across every job's budget
     * grid without bleeding to other users on a shared browser. See useScopedColumnPersistence.
     */
    "bt-object-budgetColumnState": Record<string, ColumnState[]> | null;
    /**
     * Device-local "What's New" watermark, keyed `{builderId}:{globalUserId}` so one person
     * clearing the dot on a shared computer does not clear it for the next. The value is the
     * epoch milliseconds of the newest announcement they had seen when they last opened the panel.
     */
    "bt-object-whatsNewLastSeen": Record<string, number>;
    "bt-boolean-progressInvoiceSwitchViewHidden": boolean;
    "bt-boolean-costInboxMigrationBannerDismissed": boolean;
    // Daily Logs autosave intro banner (ado-279937). Dismissal is tracked per variant so
    // dismissing one (e.g. the builder Draft "share" banner) doesn't suppress the other.
    "bt-boolean-dailyLogAutosaveBannerAddDismissed": boolean;
    "bt-boolean-dailyLogAutosaveBannerEditDismissed": boolean;
    "bt-object-mfaNudgeDismissal": Record<
        number,
        {
            userId: number;
            checkedAt: number;
            wasShown: boolean;
            dismissed: boolean;
            graceDaysRemaining: number;
            forceMfa?: boolean;
        }
    > | null;
    "bt-object-timeClockGroupingPrefs": {
        selectedGroup: ShiftGroupBy;
        allGroupsExpanded: boolean;
    };
    /**
     * The AG Grid "New experience" toggle while the legacy sunset banner app default is off. `null`
     * means never chosen. Access through `getStoredWorksheetAGGridActive`.
     */
    "bt-boolean-isAGGridActive": boolean | null;
    /**
     * The AG Grid toggle once the legacy sunset banner app default is on. `null` (never chosen)
     * reads as ON.
     */
    "bt-boolean-worksheetAGGridActive": boolean | null;
    // Epoch ms of when this user turned the Research Preview on, so the opt-out telemetry can
    // report how long they stayed in it. Per-device, so an opt-out on another device reports no
    // duration rather than a wrong one.
    "bt-number-infoArchOptedInAt": number | null;
}

export const localStorageDefaultValues: ILocalStorage = {
    "bt-boolean-worksheetTaxBannerDismissed": false,
    "bt-boolean-btMFAWarningBannerDismissed": false,
    "bt-boolean-selectionsTrialAlertDismissed": false,
    "bt-boolean-hideScheduleAlerts": false,
    "bt-boolean-logoCollapsed": window.innerHeight < 800,
    "bt-boolean-showSchedulePhases": false,
    "bt-boolean-showScheduleNotes": false,
    "bt-boolean-hideEditorDebug": false,
    "bt-boolean-obpDontShowAgain": false,
    "bt-boolean-disableAllAnalytics": null,
    "bt-boolean-hasGivenEnterpriseMarketingFeedback": null,
    "bt-boolean-hasUnreadNotifications": null,
    "bt-boolean-hideClientUpdateClientVisibilityDisclaimer": false,
    "bt-boolean-completedSubtasksOpen": true,
    "bt-boolean-taskAssistantVisible": true,
    "bt-number-lastOauthBuilderSelected": null,
    "bt-number-recentLoginType": undefined,
    "bt-number-sidebarWidth": 310,
    "bt-boolean-hasUnreadChat": null,
    "bt-object-autoDismissEmptyState": {},
    "bt-object-filterPreferences": {},
    "bt-object-dangerousJobPickerState": {
        keywordSearch: "",
        filters: "",
        templateFilters: "",
        isTemplateMode: false,
        selectedJobIds: [],
        isAllJobsSelected: false,
        allBuildersSelected: false,
    },
    "bt-object-ganttChartLocalSettings": {
        showCriticalPath: false,
        showBaseline: false,
        showJobs: false,
        showPhases: false,
        zoomLevel: GanttZoomLevels.Day,
        gridWidth: DEFAULT_GRID_WIDTH,
        columns: [
            GanttColumnType.Title,
            GanttColumnType.Job,
            GanttColumnType.StartDate,
            GanttColumnType.Workdays,
            GanttColumnType.QuickEdit,
            GanttColumnType.NewSchedule,
        ],
    },
    "bt-object-ganttPrintState": {},
    "bt-object-headerInfo": null,
    "bt-object-pageSizePreferences": {},
    "bt-object-clientObservableProcessStatusStorage": {},
    "bt-object-submittalExtractionActive": {},
    "bt-object-chatDraftValues": {},
    "bt-object-billUploadQueue": {},
    "bt-object-pendingWebviewLineItemsToInvoice": null,
    "bt-objectArray-sawmillEvents": [],
    "bt-objectArray-sawmillEventsV2": [],
    "bt-objectArray-searchBarRecentResults": [],
    "bt-objectArray-globalSearchRecentlyViewed": [],
    "bt-objectArray-searchDialogRecents": [],
    "bt-objectArray-searchAskRecentQuestions": [],
    "bt-string-jobPickerPreference": "open",
    "bt-string-monthViewPreference": "collapsed",
    "bt-string-selectedDocumentListView": "Table",
    "bt-string-selectedPhotoListView": "Tile",
    "bt-string-selectedVideoListView": "Tile",
    "bt-object-selectedPhotoSort": {},
    "bt-object-selectedDocumentSort": {},
    "bt-object-selectedVideoSort": {},
    "bt-string-selectedBrowseBTListView": "Table",
    "bt-string-selectedViewAllAttachmentsListView": "Table",
    "bt-string-selectedAttachmentsContainerView": "Tile",
    "bt-string-hideBrowserNotSupportedTime": null,
    "bt-object-showMoreState": {
        [EntityTypes.Schedule]: false,
    },
    "bt-object-notificationPermissionsPromptState": {
        state: PromptState.New,
    },
    "bt-string-paymentVerificationOauthRedirectUrl": null,
    "bt-string-paymentServiceAuthToken": null,
    "bt-object-oauthRedirectState": null,
    "bt-numberArray-jobIDsToPrint": [],
    "bt-numberArray-proposalCollapsedGroups": [],
    "bt-object-tableSettings": {},
    "bt-number-expectedBudgetReportOption": null,
    "bt-boolean-debugReactQuery": null,
    "bt-boolean-showOwnerInvoiceEditOptions": true,
    "bt-object-btCollapsePrefs": {},
    "bt-boolean-projectedCostTypeConfirmHidden": false,
    "bt-boolean-currentCostsWarningHidden": false,
    "bt-boolean-hidePostBTPaymentsBanner": false,
    "bt-number-selectedProposalTemplateID": null,
    "bt-boolean-hideOutOfSyncAcctBanner": false,
    "bt-objectArray-previouslyViewedReceipts": [],
    "bt-objectArray-recentlyReviewReceipts": [],
    "bt-numberArray-proposalFavoritedOptions": [],
    "bt-object-oAuthStateParam": null,
    "bt-string-newAppDetectedDate": null,
    "bt-object-oprUserPreferences": {},
    "bt-object-lastSelectedInvoicePreferences": {},
    "bt-object-addInvoiceLineItemPreferences": {},
    "bt-object-previousAuthStoreInfo": null,
    "bt-string-smsAccessToken": null,
    "bt-string-smsAccessTokenLastFour": null,
    "bt-boolean-useDraftMode": null,
    "bt-object-entityReactionMap": {},
    "bt-string-selectedNotificationTab": "You",
    "bt-number-selectedBuilderId": null,
    "bt-number-billLeftColumnWidth": 75,
    "bt-number-billFileAttachmentsColumnWidth": 35,
    "bt-number-progressInvoiceSupportingDocsColumnWidth": 35,
    "bt-number-billSidePanelColumnWidth": 25,
    "bt-boolean-billFileAttachmentsColumnCollapsed": false,
    "bt-number-invoiceCustomizeDrawerWidth": 320,
    "bt-boolean-invoiceCustomizeDrawerCollapsed": false,
    "bt-object-selectedJobsiteIdWithBuilder": null,
    "bt-numberArray-changeOrderColumnDisplayPreferences": null,
    "bt-object-quickAddClickCounts": {},
    "bt-string-planViewerSidebarTab": "thumbnails",
    "bt-boolean-planViewerShowAnnotations": true,
    "bt-string-billDefaultTab": null,
    "bt-string-specificationViewPreference": null,
    "bt-object-billLineItemContainerV2ColumnState": null,
    "bt-object-selectionOptionsColumnState": null,
    "bt-string-selectionOptionsViewPreference": null,
    "bt-object-budgetColumnState": null,
    "bt-object-whatsNewLastSeen": {},
    "bt-boolean-progressInvoiceSwitchViewHidden": false,
    "bt-boolean-costInboxMigrationBannerDismissed": false,
    "bt-boolean-dailyLogAutosaveBannerAddDismissed": false,
    "bt-boolean-dailyLogAutosaveBannerEditDismissed": false,
    "bt-object-mfaNudgeDismissal": null,
    "bt-object-timeClockGroupingPrefs": {
        selectedGroup: ShiftGroupBy.None,
        allGroupsExpanded: false,
    },
    "bt-boolean-isAGGridActive": null,
    "bt-boolean-worksheetAGGridActive": null,
    "bt-number-infoArchOptedInAt": null,
};

/**
 * Note to devs: do NOT store classes in SessionStorage, use interfaces instead.
 * Reminder only some data can be stringified and stored (see detectDataThatCantBeStored for more info).
 * See https://btwiki.atlassian.net/wiki/spaces/dv/pages/3366355107/Browser+Storage+Patterns for more details
 */
export interface ISessionStorage {
    "bt-boolean-hasViewedTradeAgreement": boolean;
    "bt-boolean-hideEditorDebug": boolean;
    "bt-numberArray-photoPreviewListOfDocInstanceIds": number[];
    "bt-numberArray-photoPreviewListOfJobIds": number[];
    "bt-numberArray-webviewSelectedJobIds": number[];
    "bt-object-tradeAgreementValues": ITradeAgreementSessionValues | null;
    "bt-object-dangerousJobPickerState": IJobPickerPersistantState | null;
    "bt-object-notificationPermissionsPromptState": NotificationPermissionPromptState;
    "bt-string-lastTabTitleBeforeTemporaryTitle": string | null;
    "bt-objectArray-CheckoutReferral": ICheckOutReferralState | null;
    "bt-object-stepUpContext": IStepUpContext | null;
    /**
     * Set when a time clock webview is loaded with ?hideSegmentedControl=true so the
     * native app can own the navigation. Persisted because the param is stripped from the
     * URL as soon as the user opens a nested route (clock in/out, shift details), so it
     * can't be re-read after the first load. Session-scoped so it clears when the webview
     * session ends. See TimeClockWebviewControlsContext; mirrors
     * bt-numberArray-webviewSelectedJobIds in WebviewPageLayout.
     */
    "bt-boolean-timeClockWebviewHideSegmentedControl": boolean;
    /**
     * Set when a time clock webview is loaded with ?hideCloseButton=true so the native app
     * can own shift details' close/back affordance itself. Kept separate from
     * bt-boolean-timeClockWebviewHideSegmentedControl because the native versions sending
     * the two params ship independently. Persisted and scoped the same way.
     * See TimeClockWebviewControlsContext.
     */
    "bt-boolean-timeClockWebviewHideCloseButton": boolean;
    /**
     * Ask conversations, so a reload, Back or Forward carries one on instead
     * of asking its first question again. Per tab, like the conversation.
     */
    "bt-objectArray-searchAskTranscripts": IStoredAskTranscript[];
}
const sessionStorageDefaultValues: ISessionStorage = {
    "bt-boolean-hasViewedTradeAgreement": false,
    "bt-boolean-hideEditorDebug": false,
    "bt-numberArray-photoPreviewListOfDocInstanceIds": [],
    "bt-numberArray-photoPreviewListOfJobIds": [],
    "bt-numberArray-webviewSelectedJobIds": [],
    "bt-object-tradeAgreementValues": null,
    "bt-object-dangerousJobPickerState": null,
    "bt-object-notificationPermissionsPromptState": {
        state: PromptState.New,
    },
    "bt-string-lastTabTitleBeforeTemporaryTitle": null,
    "bt-objectArray-CheckoutReferral": null,
    "bt-object-stepUpContext": null,
    "bt-boolean-timeClockWebviewHideSegmentedControl": false,
    "bt-boolean-timeClockWebviewHideCloseButton": false,
    "bt-objectArray-searchAskTranscripts": [],
};

function isStorageSupported(): boolean {
    try {
        const storage = window.localStorage;
        storage.setItem("isStorageSupportedTest", "test value");
        storage.removeItem("isStorageSupportedTest");
        return true;
    } catch (e) {
        return false;
    }
}

const allowedTypes = {
    Object: true,
    Boolean: true,
    Array: true,
    Number: true,
    String: true,
};

const allowedTypeKeys = [
    "boolean",
    "number",
    "string",
    "object",
    "numberArray",
    "stringArray",
    "objectArray",
];

/**
 * JSON.stringify can only store some values, you should not store values in localstorage that can't be stored
 * For example you should never try and store the following: function, dates, Infinity, Set, Map, BigInt, ...
 * Also make sure you store a base object instead of a class. Only properties of the class can be stored
 */
function detectDataThatCantBeStored(this: any, key: any, value: any) {
    // classes can have a custom toJSON method that is called BEFORE this method is run (example Date). We want to filter values before toJSON is run - https://stackoverflow.com/questions/21034760/strange-behaviour-in-json-stringify-with-replacer-function
    const rawValue = this[key];
    const isEmpty = rawValue === undefined || rawValue === null;
    const isPrimitiveType =
        allowedTypes[rawValue?.constructor?.name as keyof typeof allowedTypes] === true;
    const isPlainObjectWithoutConstructor =
        typeof rawValue === "object" && rawValue !== null && !("constructor" in rawValue);
    const isSupportedType =
        isEmpty || isPrimitiveType || isPlainObjectWithoutConstructor || moment.isMoment(rawValue);

    if (!isSupportedType) {
        throw new Error(
            `Attempted to store data in localstorage that cannot be stringified - key: ${key}, value: ${rawValue} - if you are trying to use a class instead use a plain object. LocalStorage cannot store complex types like classes, dates, or functions`,
        );
    }

    return value;
}

const throwDevelopmentError = (msg: string) => {
    if (isUnitTest() || import.meta.env.MODE === "development") {
        throw msg;
    }
};

function logErrorIfNeeded(error: any) {
    const storageSupported = isStorageSupported();

    console.error(error);
    if (storageSupported) {
        // storage is supported, but we got an exception, report it
        reportError(error);
    }
    throwDevelopmentError(error);
}

export const checkFullyQualifiedKey = (key: string) => {
    const keyTokens = key.split("-");
    if (keyTokens.length !== 3) {
        logErrorIfNeeded(
            `Key [${key}] did not conform to proper naming convention of "bt-<type>-<key>"`,
        );
        return;
    }

    const [prefix, typeString] = keyTokens;
    if (prefix !== "bt") {
        logErrorIfNeeded(`Key [${key}] did not start with "bt-"`);
        return;
    }
    if (!allowedTypeKeys.includes(typeString)) {
        logErrorIfNeeded(
            `Key [${key}] did not include one of the allowed types: [${allowedTypeKeys.join(", ")}]`,
        );
    }
};

// todo have util to mock out local storage and clear it automatically between each test
/**
 * A wrapper around localstorage/sessionstorage - this wrapper provides a strongly typed way of accessing storage
 * @example
 * // storage item will be loaded, if no item exists or the user has storage off the default will be returned
 * const thing = Storage.get("JobPickerPreference");
 */
class BTStorage<StorageType> {
    constructor(storageType: Storage, defaultValues: StorageType) {
        this.storage = storageType;
        this.defaultValues = defaultValues;
        this.storageListeners = new WeakMap<any, any>();
    }

    private storage: Storage;
    private defaultValues: StorageType;
    private storageListeners: WeakMap<any, any>;

    get<Key extends keyof StorageType>(key: Key): StorageType[Key] {
        checkFullyQualifiedKey(key.toString());
        try {
            const value = this.storage.getItem(key as string);

            if (value === null || value === "undefined") {
                return this.defaultValues[key];
            }
            return JSON.parse(value);
        } catch (error) {
            if (key === "jobPickerPreference") {
                // could not parse jobPickerPreference because it was not stored as a serialized string
                // todo remove once react jobpicker is fully live
                this.storage.removeItem("jobPickerPreference");
                return this.defaultValues[key];
            }

            logErrorIfNeeded(error);
            return this.defaultValues[key];
        }
    }

    set<Key extends keyof StorageType>(key: Key, value: StorageType[Key]): void {
        checkFullyQualifiedKey(key.toString());
        // placed outside of the try so this will throw when devs store non-serializable data
        const newValue = JSON.stringify(value, detectDataThatCantBeStored);

        // we have made the decision to not polyfill localstorage until (if) it becomes a larger issue
        // if we cant store items we will simply ignore the save and continue to return the default value
        try {
            const oldValue = this.get(key);
            this.storage.setItem(key as string, newValue);

            // Some browsers do not trigger storage events within the same window. Manually trigger the event
            // see addChangeListener/removeChangeListener for more info
            window.dispatchEvent(
                new StorageEvent("storage", {
                    key: key as string,

                    // to match the existing events we need to stringify the old key
                    oldValue: JSON.stringify(oldValue),
                    newValue: newValue,
                    url: document.URL,
                }),
            );
        } catch (error) {
            // todo catch QuotaExceededError - the user has storage turned off - don't log
            // todo detect when the user has hit the storage quota (most of the time 5MB)
            logErrorIfNeeded(error);
        }
    }

    /**
     * Fires a change even when the storage key is changed. Useful for listening to storage change from other tabs
     */
    addChangeListener<Key extends keyof StorageType>(
        key: Key,
        callback: (oldValue: StorageType[Key], newValue: StorageType[Key]) => void,
    ): void {
        const filteredCallback = (event: StorageEvent) => {
            // only fire callback if the key is the item being changed
            if (event.key === key) {
                const oldValueParsed: StorageType[Key] =
                    event.oldValue === null ? this.defaultValues[key] : JSON.parse(event.oldValue);
                const newValueParsed: StorageType[Key] =
                    event.newValue === null ? this.defaultValues[key] : JSON.parse(event.newValue);

                callback(oldValueParsed, newValueParsed);
            }
        };

        // we store a reference to the generated wrapped callback so we can remove it later if need be
        this.storageListeners.set(callback, filteredCallback);
        window.addEventListener("storage", filteredCallback);
    }

    /**
     * Removes a existing storage change listener
     */
    removeChangeListener<Key extends keyof StorageType>(
        key: Key,
        callback: (oldValue: StorageType[Key], newValue: StorageType[Key]) => void,
    ): void {
        const filteredCallback = this.storageListeners.get(callback);

        window.removeEventListener("storage", filteredCallback);
    }

    /**
     * Removes a item from local storage
     * @param key The item to remove
     */
    remove<Key extends keyof StorageType>(key: Key): void {
        checkFullyQualifiedKey(key.toString());
        try {
            this.storage.removeItem(key as string);
        } catch (error) {
            logErrorIfNeeded(error);
        }
    }

    /**
     * Clears local and session storage for service cloud items
     * @param key The storage key to match
     * @param salesforceOrgId salesforce org id to be prepended to the key
     */
    removeServiceCloudStorageItems(salesforceOrgId: string): void {
        try {
            const searchKey = `${salesforceOrgId}${LocalStorageKeys.DynamicServiceCloudKey}`;
            this.storage.removeItem(searchKey);
            sessionStorage.removeItem(searchKey);
        } catch (error) {
            logErrorIfNeeded(error);
        }
    }
}

// Prefer passing the key as an inline string literal at the call site
// (e.g. BTLocalStorage.get("bt-boolean-...")) rather than hoisting it into a
// const. Both are equally type-safe — a const string keeps its literal type —
// but the inline literal triggers IDE autocomplete of every valid key, which a
// const hides behind an indirection.
export const BTLocalStorage = new BTStorage<ILocalStorage>(localStorage, localStorageDefaultValues);

export const BTSessionStorage = new BTStorage<ISessionStorage>(
    sessionStorage,
    sessionStorageDefaultValues,
);

export function useReadLocalStorage<Key extends keyof ILocalStorage>(
    key: Key,
): ILocalStorage[Key] | null {
    return useReadLocalStorageInternal<ILocalStorage[Key]>(key);
}
